/**
 * LocationGroupCard — collapsible list of items at a location.
 *
 * Renders inside an AssistantBubble when a `find_by_location` tool
 * resolved.  Shows the location header + a horizontal scroll of
 * item chips (compact, no thumbnail since the BE typically returns
 * `primary_photo_url: null` for this tool).  Tap any chip to open
 * ItemDetail.
 */

import React from 'react';
import {
  FlatList,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { fpColors } from '../../../../styles/theme';
import type { LocationGroupArtifact } from '../../api/parseArtifactsFromToolEvent';

interface Props {
  artifact: LocationGroupArtifact;
}

export const LocationGroupCard: React.FC<Props> = ({ artifact }) => {
  const navigation = useNavigation<any>();
  if (artifact.items.length === 0) return null;
  return (
    <View style={styles.card} testID={`founsi-location-${artifact.location}`}>
      <View style={styles.header}>
        <MaterialIcons name="place" size={14} color={fpColors.primary} />
        <Text style={styles.headerText} numberOfLines={1}>
          {artifact.items[0].location || artifact.location}
        </Text>
        <Text style={styles.count}>
          {artifact.items.length === 1
            ? '1 item'
            : `${artifact.items.length} items`}
        </Text>
      </View>
      <FlatList
        data={artifact.items}
        horizontal
        showsHorizontalScrollIndicator={false}
        keyExtractor={(it) => `${it.item_id}`}
        contentContainerStyle={styles.chipsRow}
        keyboardShouldPersistTaps="handled"
        renderItem={({ item }) => (
          <Pressable
            style={({ pressed }) => [
              styles.chip,
              pressed && styles.chipPressed,
            ]}
            onPress={() =>
              navigation.navigate('ItemDetail', { itemId: item.item_id })
            }
            accessibilityRole="button"
            accessibilityLabel={`Open ${item.item_name}`}
            testID={`founsi-location-item-${item.item_id}`}
          >
            <Text style={styles.chipText} numberOfLines={1}>
              {item.item_name}
            </Text>
          </Pressable>
        )}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    marginBottom: 8,
    paddingVertical: 8,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingBottom: 6,
  },
  headerText: {
    flex: 1,
    fontSize: 12,
    fontWeight: '600',
    color: fpColors.onSurface,
  },
  count: {
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
  },
  chipsRow: {
    paddingHorizontal: 10,
    gap: 6,
  },
  chip: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: fpColors.surfaceContainerLow,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    maxWidth: 200,
  },
  chipPressed: {
    backgroundColor: fpColors.surface,
  },
  chipText: {
    fontSize: 12,
    color: fpColors.onSurface,
  },
});

export default LocationGroupCard;
